import * as React from "react";
import i18 from "i18next";
import { debounce, DebounceCounter } from "src/common/debounce";
import { langList } from "src/lang/langList";
import { Button } from "antd";
import { rest } from "src/common/rest";
import { apiObjUrl } from "src/common/apiUrl";
import { onError } from "src/common/onError";
import styles from "./LangSwitcher.module.less";

const makeRef = (href: string, lang: string): string =>
  href.replace(`/${i18.language}/`, `/${lang}/`);

let observer: MutationObserver | undefined;
const delayCounter: DebounceCounter = {};

export const LangSwitcher: React.FC = () => {
  const [href, setHref] = React.useState("");
  React.useEffect(() => {
    // Необходимо отслеживать изменение текущего урла страницы, чтобы обновлять урлы ссылок.
    observer = new MutationObserver(() => {
      debounce(delayCounter, 50, () => {
        setHref(window.location.href);
      });
    });
    const root = document.getElementById("root");
    if (root) {
      observer.observe(root, {
        childList: true,
        subtree: true,
      });
    }
    return () => {
      if (observer) {
        observer.disconnect();
        observer = undefined;
      }
    };
  }, []);
  const [openLang, setOpenLang] = React.useState(false);
  const onUserClick = () => setOpenLang(!openLang);
  const onKey = React.useCallback((e: KeyboardEvent) => {
    if (e.code === "Escape") setOpenLang(false);
  }, []);
  const stopMouse = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);
  const onMouseDown = React.useCallback(() => {
    setOpenLang(false);
  }, []);
  React.useEffect(() => {
    window.addEventListener("keyup", onKey);
    window.addEventListener("mousedown", onMouseDown);
    return () => {
      window.removeEventListener("keyup", onKey);
      window.removeEventListener("mousedown", onMouseDown);
    };
  }, []);
  const onLangChange = (languageCode: string) => {
    rest
      .put(apiObjUrl("/users/change-user-language"), undefined, {
        params: { languageCode },
      })
      .then(() => {
        window.location.href = makeRef(href, languageCode);
      })
      .catch(onError);
  };
  return (
    <div
      role="presentation"
      className={styles.langMenu}
      onClick={onUserClick}
      onMouseDown={stopMouse}
      data-testid="langSwitcher"
    >
      <span className={styles.langName}>{i18.language.toUpperCase()}</span>

      {openLang && (
        <menu
          role="presentation"
          className={styles.menu}
          onClick={() => setOpenLang(false)}
          onMouseDown={stopMouse}
        >
          <ul>
            {langList.map(({ label, value }) => (
              <li
                key={value}
                className={i18.language === value ? styles.active : ""}
              >
                <Button
                  type="text"
                  onClick={() => onLangChange(value)}
                  data-testid={`langSwitcher-${value}`}
                >
                  {label.toUpperCase()}
                </Button>
              </li>
            ))}
          </ul>
        </menu>
      )}
    </div>
  );
};
